/**
 * Stream Error Handler
 *
 * Centralized error handling for SSE (Server-Sent Events) chat streaming
 * with structured logging, user feedback, and error recovery.
 *
 * @module StreamErrorHandler
 */

import { message as antMessage } from 'antd';
import { logger } from '@ui/utils/logger';
import type { SSEError, SSEErrorType } from '../types';

// =============================================================================
// Error Context Types
// =============================================================================

/**
 * Context information for stream errors
 */
export interface StreamErrorContext {
    operation: string;
    conversationId: string;
    correlationId?: string;
    patientId?: string | null;
    timestamp: number;
    url?: string;
    statusCode?: number;
}

/**
 * Enhanced stream error with full context
 */
export interface EnhancedStreamError extends SSEError {
    context: StreamErrorContext;
    userMessage: string;
    isRecoverable: boolean;
    shouldRetry: boolean;
}

// =============================================================================
// Error Classification
// =============================================================================

/**
 * Determine if an error is recoverable
 */
function isRecoverableError(type: SSEErrorType, statusCode?: number): boolean {
    // Network errors and 5xx errors are usually recoverable
    if (type === 'connection_failed') return true;
    if (statusCode && statusCode >= 500 && statusCode < 600) return true;

    // Parse errors and 4xx client errors are not recoverable
    if (type === 'parse_error') return false;
    if (statusCode && statusCode >= 400 && statusCode < 500) return false;

    // Unknown errors default to non-recoverable
    return false;
}

/**
 * Determine if an error should trigger a retry
 */
function shouldRetryError(error: EnhancedStreamError, retryCount: number, maxRetries: number): boolean {
    if (retryCount >= maxRetries) return false;
    if (!error.isRecoverable) return false;
    if (error.type === 'abort_error') return false; // User cancelled
    return true;
}

/**
 * Get user-friendly error message
 */
function getUserMessage(type: SSEErrorType, statusCode?: number, errorMessage?: string): string {
    switch (type) {
        case 'connection_failed':
            return 'Failed to connect to the server. Please check your internet connection and try again.';

        case 'http_error':
            if (statusCode === 401) return 'Session expired. Please refresh the page and log in again.';
            if (statusCode === 403) return 'You do not have permission to access this conversation.';
            if (statusCode === 404) return 'Conversation not found. It may have been deleted.';
            if (statusCode === 429) return 'Too many requests. Please wait a moment and try again.';
            if (statusCode && statusCode >= 500) return 'Server error. Please try again in a few moments.';
            return `Request failed (${statusCode}). Please try again.`;

        case 'parse_error':
            return 'Received invalid data from server. The conversation may be corrupted.';

        case 'abort_error':
            return 'Stream was cancelled.';

        case 'cancelled_by_user':
            return 'Stream cancelled by user.';

        case 'event_dispatch_failed':
            return 'Failed to update UI. Please refresh the page.';

        case 'unknown_error':
        default:
            return errorMessage || 'An unexpected error occurred. Please try again.';
    }
}

// =============================================================================
// Error Creation
// =============================================================================

/**
 * Create an enhanced stream error with full context
 */
export function createStreamError(
    type: SSEErrorType,
    originalError: unknown,
    context: StreamErrorContext,
    statusCode?: number
): EnhancedStreamError {
    const errorMessage = originalError instanceof Error ? originalError.message : String(originalError);

    const error: EnhancedStreamError = {
        type,
        message: errorMessage,
        originalError,
        conversationId: context.conversationId,
        timestamp: Date.now(),
        context,
        userMessage: getUserMessage(type, statusCode, errorMessage),
        isRecoverable: isRecoverableError(type, statusCode),
        shouldRetry: false, // Will be set by retry logic
    };

    return error;
}

// =============================================================================
// Error Logging
// =============================================================================

/**
 * Log structured error with full context
 */
export function logStreamError(error: EnhancedStreamError, level: 'error' | 'warn' = 'error'): void {
    const logData = {
        type: error.type,
        message: error.message,
        operation: error.context.operation,
        conversationId: error.context.conversationId,
        correlationId: error.context.correlationId,
        patientId: error.context.patientId,
        statusCode: error.context.statusCode,
        url: error.context.url,
        timestamp: new Date(error.timestamp).toISOString(),
        isRecoverable: error.isRecoverable,
        originalError: error.originalError,
    };

    if (level === 'error') {
        logger.error('[Stream Error]', logData);
    } else {
        logger.warn('[Stream Error]', logData);
    }
}

// =============================================================================
// User Feedback
// =============================================================================

/**
 * Show user-friendly error message
 */
export function showStreamError(error: EnhancedStreamError, options?: { duration?: number }): void {
    const { duration = 5 } = options || {};

    if (error.type === 'abort_error' || error.type === 'cancelled_by_user') {
        // Don't show notification for user-initiated cancellations
        return;
    }

    if (error.isRecoverable) {
        antMessage.warning({
            content: error.userMessage,
            key: `stream-error-${error.context.conversationId}`,
            duration,
        });
    } else {
        antMessage.error({
            content: error.userMessage,
            key: `stream-error-${error.context.conversationId}`,
            duration,
        });
    }
}

// =============================================================================
// Validation
// =============================================================================

/**
 * Validate stream packet structure
 */
export interface PacketValidationResult {
    isValid: boolean;
    errors: string[];
}

export function validateStreamPacket(packet: unknown): PacketValidationResult {
    const errors: string[] = [];

    if (typeof packet !== 'object' || packet === null) {
        return { isValid: false, errors: ['Packet must be an object'] };
    }

    const pkt = packet as Record<string, unknown>;

    // Optional response field
    if ('response' in pkt && pkt.response !== undefined) {
        if (typeof pkt.response !== 'object' || pkt.response === null) {
            errors.push('response must be an object');
        } else {
            const response = pkt.response as Record<string, unknown>;

            // Validate assistant_message if present
            if ('assistant_message' in response && response.assistant_message !== undefined) {
                const msg = response.assistant_message;
                if (typeof msg !== 'string' && (typeof msg !== 'object' || msg === null)) {
                    errors.push('assistant_message must be string or object');
                }
            }

            // Validate timestamp if present
            if ('timestamp' in response && response.timestamp !== undefined) {
                if (typeof response.timestamp !== 'string' && typeof response.timestamp !== 'number') {
                    errors.push('timestamp must be string or number');
                }
            }

            // Validate patient_id if present (allow null for non-patient queries)
            if ('patient_id' in response && response.patient_id !== undefined && response.patient_id !== null) {
                if (typeof response.patient_id !== 'string') {
                    errors.push('patient_id must be string');
                }
            }
        }
    }

    return {
        isValid: errors.length === 0,
        errors,
    };
}

// =============================================================================
// Retry Logic
// =============================================================================

/**
 * Calculate exponential backoff delay
 */
export function calculateBackoffDelay(retryCount: number, baseDelay = 1000, maxDelay = 10000): number {
    const delay = Math.min(baseDelay * Math.pow(2, retryCount), maxDelay);
    // Add jitter (±20%)
    const jitter = delay * 0.2 * (Math.random() * 2 - 1);
    return Math.floor(delay + jitter);
}

/**
 * Check if error should trigger retry
 */
export function shouldRetry(error: EnhancedStreamError, retryCount: number, maxRetries = 3): boolean {
    return shouldRetryError(error, retryCount, maxRetries);
}

// =============================================================================
// HTTP Error Handling
// =============================================================================

/**
 * Create HTTP error from response
 */
export function createHTTPError(
    statusCode: number,
    statusText: string,
    context: StreamErrorContext
): EnhancedStreamError {
    return createStreamError(
        'http_error',
        new Error(`HTTP ${statusCode}: ${statusText}`),
        { ...context, statusCode },
        statusCode
    );
}

// =============================================================================
// Exports
// =============================================================================

/**
 * Centralized stream error handler
 */
export const streamErrorHandler = {
    create: createStreamError,
    createHTTP: createHTTPError,
    log: logStreamError,
    show: showStreamError,
    validate: validateStreamPacket,
    shouldRetry,
    calculateBackoff: calculateBackoffDelay,
} as const;

export default streamErrorHandler;
